import React, { useState, useEffect } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { TextField, MenuItem, Box, Button } from "@mui/material";
import Link from "@mui/material/Link";

function RecordsTable({ fileId, onRecordSelect, filename }) {
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState({
    search: "",
    status: "",
  });

  const pageSize = 20;

  useEffect(() => {
    if (!fileId) return;

    const query = new URLSearchParams({
      page: page + 1, // API expects 1-based page
      limit: pageSize,
      ...filters,
    });

    fetch(`/api/files/${fileId}/records?${query.toString()}`)
      .then((res) => res.json())
      .then((data) => {
        setRecords(data.records);
        setTotal(data.total);
      })
      .catch((err) => {
        console.error("Error fetching records:", err);
        setRecords([]);
        setTotal(0);
      });
  }, [fileId, page, filters]);

  const columns = [
    { field: "id", headerName: "ID", width: 80 },
    { field: "email", headerName: "Email", flex: 1 },
    { field: "name", headerName: "Name", width: 150 },
    {
      field: "conversation_url",
      headerName: "Gladly URL",
      width: 150,
      renderCell: (params) => (
        <Link
          href={params.value}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary"
          onClick={(event) => {
            event.stopPropagation(); // ✅ Prevent triggering onRowClick
          }}
        >
          {params.value ? "View Conversation" : "-"}
        </Link>
      ),
    },
    { field: "status", headerName: "Status", width: 120 },
    { field: "errorMessage", headerName: "Message", width: 120 },
    { field: "retryCount", headerName: "Retry", width: 120 },
    { field: "modified_at", headerName: "Updated@", width: 150 },
    { field: "created_at", headerName: "Created@", width: 150 },
  ];

  const handleExportCSV = async () => {
    const response = await fetch(`/api/files/${fileId}/records?page=1&limit=100000`); // backend returns all rows
      
    const allRows = await response.json();
      
    const csvRows = allRows.records.map(obj =>   
      `${obj.id},${obj.email},${obj.name},${obj.conversation_url},${obj.status},${Object.entries(obj.json).map(([key, value]) => (`${key}: ${value}`)).join("; ")},${obj.errorMessage},${obj.retryCount},${obj.modified_at},${obj.created_at}`
    );
    const csvHeader = "ID,Email,Name,Gladly URL,Status,File Data,Error Message,Retry,Updated At,Created At";
    const csvContent = [csvHeader, ...csvRows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const timestamp = new Date()
        .toISOString()
        .replace(/[:.-]/g, '') // remove invalid characters for filenames
        .slice(0, 15); // e.g. 20251023T1455
    const a = document.createElement("a");
    a.href = url;
    a.download = `records_${filename}_${timestamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box marginTop={2}>
      {/* Filters */}
      <Box display="flex" gap={2} marginBottom={2} alignItems="center">
        <TextField
          label="Search (email/name)"
          value={filters.search}
          onChange={(e) => {
            setPage(0);
            setFilters({ ...filters, search: e.target.value });
          }}
        />
        <TextField
          select
          label="Status"
          value={filters.status}
          onChange={(e) => {
            setPage(0);
            setFilters({ ...filters, status: e.target.value });
          }}
          style={{ width: 150 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="completed">Completed</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
          <MenuItem value="in-progress">In-Progress</MenuItem>
        </TextField>

        {/* ✅ CSV Export button (modern approach) */}
        <Button
          variant="outlined"
          size="small"
          onClick={handleExportCSV}
          disabled={!records.length}
        >
          Export CSV
        </Button>
      </Box>

      {/* Table */}
      <div style={{ width: "100%" }}>
        <DataGrid
          rows={records}
          columns={columns}
          rowCount={total}
          pageSizeOptions={[pageSize]}
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => setPage(model.page)}
          loading={!fileId}
          getRowId={(row) => row.id}
          onRowClick={({ row }) => onRecordSelect && onRecordSelect(row)}
        />
      </div>
    </Box>
  );
}

export default RecordsTable;
